import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Project } from '@vd/types';
import { useAuth } from '../features/auth/AuthProvider';
import { inkOn, projectInitials } from '../features/projects/ProjectMark';
import { api } from './api';

/**
 * Browser tabs that say what they are (TOADO-905): with several Toado tabs
 * open, every one said "Toado" with the same icon.
 *
 * A title is its parts, most specific first, joined by " · " ("Login button
 * does nothing · Website redesign · Acme"): Chrome cuts long titles from the
 * right and searches open tabs by title, so the words someone would look for
 * come first. On a project's page the icon is the project's mark, its own
 * icon or a square of its colour with its initial, because a site's own icon
 * is what people recognise at 16 pixels. A leading "• " and a dot on the icon
 * say something changed while the tab was in the background.
 *
 * Pages that someone outside the workspace can open (sign-in, a public report
 * link, the OAuth pages, the uninstall page) never call this, so they keep the
 * plain title and icon and never show a workspace or project name.
 */

export const PLAIN_TITLE = 'Toado';
const PLAIN_ICON = '/favicon.svg';
const MAX_TICKET_TITLE = 60;

export interface TabProject { name: string; color: string; iconUrl?: string | null }

export interface TabTitle {
  /** Most specific first; empty parts are left out. */
  parts: Array<string | null | undefined | false>;
  /** End with "Toado" (the default), as every page but a ticket's does. */
  brand?: boolean;
  /** The project whose mark is the icon; the plain Toado icon without one. */
  project?: TabProject | null;
  /** Something changed while the tab was not in front. */
  unseen?: boolean;
}

/** A ticket's title as a tab shows it: cut at about 60 characters. */
export function tabTicketTitle(title: string): string {
  const clean = title.replace(/\s+/g, ' ').trim();
  return clean.length > MAX_TICKET_TITLE ? `${clean.slice(0, MAX_TICKET_TITLE - 1).trimEnd()}…` : clean;
}

export function tabTitleText({ parts, brand = true, unseen = false }: TabTitle): string {
  const words = parts.filter((part): part is string => typeof part === 'string' && part.trim().length > 0);
  if (brand || words.length === 0) words.push(PLAIN_TITLE);
  return `${unseen ? '• ' : ''}${words.join(' · ')}`;
}

function escapeXml(text: string): string {
  return text.replace(/[<>&"']/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&apos;' }[c]!));
}

/** A rounded square of the project's colour with its initial, as an SVG data address; with a dot when `dot`. */
export function projectIconSvg(project: TabProject, dot = false): string {
  const color = /^#[0-9a-f]{3,8}$/i.test(project.color) ? project.color : '#64748b';
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">`
    + `<rect width="32" height="32" rx="7" fill="${color}"/>`
    + `<text x="16" y="22.5" font-family="system-ui,sans-serif" font-size="19" font-weight="700" text-anchor="middle" fill="${inkOn(color)}">${escapeXml(projectInitials(project.name, 1))}</text>`
    + (dot ? '<circle cx="25" cy="7" r="6.5" fill="#ef4444" stroke="#fff" stroke-width="2"/>' : '')
    + '</svg>';
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

/**
 * The icon for a tab. A project with an icon of its own shows it; with
 * something unseen it shows its initial square with the dot instead, since a
 * dot cannot be drawn onto another site's picture.
 */
export function tabIconHref(project: TabProject | null | undefined, unseen: boolean): string {
  // Only a project's tabs carry the dot (a ticket, a walkthrough's review):
  // an SVG icon cannot draw the Toado icon under one.
  if (!project) return PLAIN_ICON;
  if (project.iconUrl && !unseen) return project.iconUrl;
  return projectIconSvg(project, unseen);
}

function iconLink(): HTMLLinkElement {
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  return link;
}

interface Shown { title: string; href: string; fallback: string }

/**
 * Every mounted page's title, the newest last. A ticket opened over the board
 * is newer than the board, and closing it puts the board's back, rather than
 * the plain title while the board is still open underneath.
 */
const shown: Array<{ id: number; value: Shown }> = [];
let nextId = 0;
let probe: HTMLImageElement | null = null;

function apply(): void {
  const top = shown.at(-1)?.value ?? { title: PLAIN_TITLE, href: PLAIN_ICON, fallback: PLAIN_ICON };
  document.title = top.title;
  const link = iconLink();
  if (top.href === PLAIN_ICON) link.setAttribute('type', 'image/svg+xml');
  else link.removeAttribute('type');
  link.href = top.href;
  if (probe) { probe.onerror = null; probe = null; }
  if (top.href === top.fallback || top.href.startsWith('data:') || top.href === PLAIN_ICON) return;
  // Check the project's icon loads; one that does not shows the initial.
  const image = new Image();
  image.onerror = () => { if (probe === image && shown.at(-1)?.value === top) link.href = top.fallback; };
  probe = image;
  image.src = top.href;
}

/**
 * Set this tab's title and icon while the calling page is mounted, and put
 * back what was there when it leaves. A project icon that fails to load falls
 * back to the initial square, as the project's mark does on the page.
 */
export function useTabTitle(input: TabTitle): void {
  const title = tabTitleText(input);
  const href = tabIconHref(input.project, !!input.unseen);
  const fallback = input.project ? projectIconSvg(input.project, !!input.unseen) : PLAIN_ICON;
  const [id] = useState(() => { nextId += 1; return nextId; });
  useEffect(() => {
    const value = { title, href, fallback };
    const index = shown.findIndex((entry) => entry.id === id);
    if (index >= 0) shown[index] = { id, value };
    else shown.push({ id, value });
    apply();
  }, [id, title, href, fallback]);
  useEffect(() => () => {
    const index = shown.findIndex((entry) => entry.id === id);
    if (index >= 0) shown.splice(index, 1);
    apply();
  }, [id]);
}

/**
 * Whether something changed while this tab was in the background, cleared
 * when it is looked at again. `notice()` marks it, and only while the tab is
 * hidden: what someone sees happen in front of them is not news.
 */
export function useUnseenWhileHidden(): { unseen: boolean; notice: () => void } {
  const [unseen, setUnseen] = useState(false);
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') setUnseen(false); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);
  const notice = useCallback(() => { if (document.visibilityState === 'hidden') setUnseen(true); }, []);
  return { unseen, notice };
}

/** The name of the workspace this tab is showing. */
export function useTabWorkspaceName(): string | null {
  const { companies, currentCompanyId } = useAuth();
  return (companies ?? []).find((company) => company.id === currentCompanyId)?.name ?? null;
}

/** A project of this tab's workspace, from the list the sidebar keeps. */
export function useTabProject(projectId: string | null | undefined): (TabProject & { id: string }) | null {
  const { currentCompanyId } = useAuth();
  const { data } = useQuery({
    queryKey: ['projects', currentCompanyId],
    queryFn: () => (currentCompanyId ? api.listProjects(currentCompanyId) : Promise.resolve([] as Project[])),
    enabled: !!currentCompanyId && !!projectId,
  });
  const project = projectId ? data?.find((candidate) => candidate.id === projectId) : undefined;
  return project ? { id: project.id, name: project.name, color: project.color, iconUrl: project.iconUrl ?? null } : null;
}
